import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FacilityPicker } from "components/FacilityPicker";
import { DateField } from "components/Forms/DateField";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import {
  FacilityPickerFragment,
  FacilityStatus,
  NotReportingReason,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";

import { REPORTS_QUERY } from ".";
import { GET_REPORT } from "../Report/useReport";

/**
 * This component was originally created for the fulfillment and other report
 * tables.  This interface makes it a bit more generic in that we don't have a
 * specific type that is required. The data just needs to match the following
 * interface
 */
export interface NotReportingItem {
  reason?: NotReportingReason;
  facilityId: string;
  reportingYear: number;
  tenantId: string;
  tierIIReportId?: string | null;
  // Stored reason/note on the report. When present these are used to pre-fill the
  // dialog (preferred over the facility-derived fallback below).
  notReportingReason?: NotReportingReason | null;
  notReportingReasonNote?: string | null;
  facilityDateClosed?: string | null;
  facilityDateInactive?: string | null;
  colocationReportingFacilityId?: string | null;
  // Optional (not required) on purpose: the GraphQL source types this as
  // `FacilityStatus | null | undefined`, so requiring it would force every caller
  // to coalesce `?? null` — re-introducing the per-call remap the rename removed.
  facilityStatus?: FacilityStatus | null;
  isNotReporting?: boolean | null;
  isNotReportingThroughEncamp?: boolean | null;
}

interface NotReportingModalProps<T extends NotReportingItem> {
  reports: T[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback for successful submission (e.g., to clear selections)
}

interface FormInput {
  selectedOption?: NotReportingReason;
  dateClosed?: string;
  dateInactive?: string;
  coLocatedId?: string;
  note?: string;
}

const COLOCATION_FACILITY = gql(`
  query CoLocatedFacility($id: ID!) {
    facility(id: $id) {
      ...FacilityPicker
    }
  }
`);

const BULK_UPDATE_NOT_REPORTING_REASON_MUTATION = gql(`
  mutation BulkUpdateNotReportingReason($input: NotReportingReasonInput!) {
    bulkUpdateNotReportingReason(input: $input) {
      succeeded
    }
  }
`);

// Pre-fills the EXEMPT note from the most recent prior-year EXEMPT report. The reason
// never carries over (the user must re-select it); only the note is offered.
const PRIOR_EXEMPT_NOTE = gql(`
  query PriorExemptNote($facilityId: ID!, $beforeReportingYear: Int!) {
    priorExemptNote(facilityId: $facilityId, beforeReportingYear: $beforeReportingYear)
  }
`);

export const getValidationSchema = (reportingYear: number) => {
  // For validation, we need to check that dates are before the start of the reporting year
  // since the facility was closed/inactive for the ENTIRE reporting year
  const yearStartDate = `${reportingYear}-01-01`; // Jan 1 of the reporting year as ISO string

  // Base fields are `.nullable()` so a non-selected reason's field can hold `null`
  // (e.g. when switching reasons or clearing the facility picker) without yup throwing
  // a type error; the conditional `.required()` only applies to the selected reason.
  return yup.object().shape({
    selectedOption: yup.string().required("This field is required"),
    dateClosed: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "CLOSED",
        then: yup
          .string()
          .nullable()
          .required("This field is required")
          .test(
            "date-closed-valid",
            `Date closed must be before Jan 1, ${reportingYear} (since the facility was closed for the entire reporting year)`,
            (value) => !!value && value < yearStartDate
          ),
      }),
    dateInactive: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "INACTIVE",
        then: yup
          .string()
          .nullable()
          .required("This field is required")
          .test(
            "date-inactive-valid",
            `Date inactive must be before Jan 1, ${reportingYear} (since the facility was inactive for the entire reporting year)`,
            (value) => !!value && value < yearStartDate
          ),
      }),
    coLocatedId: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "COLOCATED",
        then: yup.string().nullable().required("This field is required"),
      }),
    note: yup
      .string()
      .nullable()
      .when("selectedOption", {
        is: "EXEMPT",
        then: yup
          .string()
          .nullable()
          .trim()
          .required("A note is required when claiming exemption."),
      }),
  });
};

export function NotReportingModal<T extends NotReportingItem>(props: NotReportingModalProps<T>) {
  const { open, onClose, reports, onSuccess } = props;
  const prevRefOpen = useRef(open);
  const [coLocatedFacility, setCoLocatedFacility] = useState<FacilityPickerFragment | null>(null);

  const singleMode = reports.length === 1;

  // All selected reports should have the same reporting year for bulk operations
  // Mixed reporting years are now prevented at the UI level by disabling bulk actions
  const reportingYear = useMemo(() => {
    // All selected reports share a reporting year (bulk actions disabled across years).
    return reports[0]?.reportingYear ?? currentTierIIReportingYear;
  }, [reports]);

  const reportingYearText = reportingYear.toString();

  const { loading } = useQuery(COLOCATION_FACILITY, {
    variables: { id: reports[0].colocationReportingFacilityId ?? "" },
    skip: !singleMode || !reports[0].colocationReportingFacilityId,
    onCompleted(data) {
      if (data.facility) {
        setCoLocatedFacility(data.facility);
      }
    },
  });

  // The most recent prior-year EXEMPT note, pre-filled (in the reason change handler below)
  // when EXEMPT is selected. The reason itself never carries over year-to-year; only the note.
  const { data: priorExemptData } = useQuery(PRIOR_EXEMPT_NOTE, {
    variables: { facilityId: reports[0]?.facilityId ?? "", beforeReportingYear: reportingYear },
    skip: !singleMode || !reports[0]?.facilityId,
  });
  const priorExemptNote = priorExemptData?.priorExemptNote ?? undefined;

  const defaultValues = useMemo<FormInput>(() => {
    let defaultOption: NotReportingReason | undefined = undefined;
    if (singleMode) {
      const reportData = reports[0];
      if (reportData?.notReportingReason) {
        // Prefer the explicit stored reason over the facility-derived fallback.
        defaultOption = reportData.notReportingReason;
      } else if (reportData?.reason) {
        defaultOption = reportData.reason;
      } else {
        if (reportData?.facilityStatus === FacilityStatus.Closed) {
          defaultOption = NotReportingReason.Closed;
        } else if (reportData?.facilityStatus === FacilityStatus.Inactive) {
          defaultOption = NotReportingReason.Inactive;
        } else if (reportData?.colocationReportingFacilityId) {
          defaultOption = NotReportingReason.Colocated;
        } else if (reportData?.isNotReporting) {
          defaultOption = NotReportingReason.UnderThreshold;
        } else if (reportData?.isNotReportingThroughEncamp) {
          defaultOption = NotReportingReason.NotWithEncamp;
        } else {
          defaultOption = NotReportingReason.Closed;
        }
      }
    }
    return {
      selectedOption: defaultOption,
      dateClosed:
        singleMode && reports[0].facilityDateClosed ? reports[0].facilityDateClosed : undefined,
      dateInactive:
        singleMode && reports[0].facilityDateInactive ? reports[0].facilityDateInactive : undefined,
      coLocatedId:
        singleMode && reports[0] ? (reports[0].colocationReportingFacilityId ?? undefined) : "",
      note: singleMode ? (reports[0]?.notReportingReasonNote ?? undefined) : undefined,
    };
  }, [reports, singleMode]);

  const validationSchema = useMemo(() => getValidationSchema(reportingYear), [reportingYear]);

  const { handleSubmit, control, watch, reset, setValue, getValues } = useForm<FormInput>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues,
    mode: "onChange", // Validate on every change
  });
  const selectedOption = watch("selectedOption");

  const [bulkUpdateNotReportingReason, { loading: saving }] = useMutation(
    BULK_UPDATE_NOT_REPORTING_REASON_MUTATION,
    {
      awaitRefetchQueries: true,
      onCompleted: () => {
        alerts.success(`Saved report${props.reports.length > 1 ? "s" : ""} successfully`);
      },
      onError: (error) => {
        alerts.error("An error occurred while saving the reports", error);
      },
      refetchQueries: [REPORTS_QUERY, GET_REPORT],
    }
  );
  const alerts = useAlerts();

  useEffect(() => {
    if (prevRefOpen.current === false && open === true) {
      reset(defaultValues);
    }
    prevRefOpen.current = open;
  }, [open, reset, defaultValues]);

  const onSubmit = useCallback(
    async (input: FormInput) => {
      if (input.selectedOption) {
        const result = await bulkUpdateNotReportingReason({
          variables: {
            input: {
              facilityIds: props.reports.map((report) => report.facilityId),
              reason: input.selectedOption,
              reportingYear: reportingYear,
              dateClosed: input.dateClosed,
              dateInactive: input.dateInactive,
              coLocatedId: input.coLocatedId,
              note: input.selectedOption === NotReportingReason.Exempt ? input.note : undefined,
            },
          },
        });
        if (!result.data?.bulkUpdateNotReportingReason?.succeeded) {
          alerts.error("Error saving reports.");
        } else {
          // Clear form state after successful submission
          reset();
          // Call success callback (e.g., to clear grid selections)
          onSuccess?.();
        }
        onClose();
      }
    },
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
    [bulkUpdateNotReportingReason, props.reports, alerts, onClose, onSuccess, reset]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      title="Set Facility as Not Reporting"
      primaryActionButtonProps={{
        onClick: handleSubmit(onSubmit),
        loading: saving,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLabel component="legend">Select the reason this facility is not reporting:</FormLabel>
        <Controller
          name="selectedOption"
          control={control}
          defaultValue={selectedOption}
          rules={{ required: "This field is required" }}
          render={({ field }) => (
            <RadioGroup
              {...field}
              onChange={(event, value) => {
                field.onChange(value);
                // Carry the prior-year EXEMPT note forward the moment EXEMPT is chosen,
                // without clobbering a note the user has already entered. Done in this
                // change handler (not an effect) so it reacts to the user's selection.
                if (
                  value === NotReportingReason.Exempt &&
                  priorExemptNote &&
                  !getValues("note")?.trim()
                ) {
                  setValue("note", priorExemptNote, { shouldValidate: true });
                }
              }}
            >
              <FormControlLabel
                value={NotReportingReason.Closed}
                control={<Radio />}
                label={`This facility was closed for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
              />
              {selectedOption === NotReportingReason.Closed && (
                <DateField name="dateClosed" label="Date Closed" control={control} />
              )}

              <FormControlLabel
                value={NotReportingReason.Inactive}
                control={<Radio />}
                label={`This facility was inactive for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
              />
              {selectedOption === NotReportingReason.Inactive && (
                <DateField name="dateInactive" label="Date Inactive" control={control} />
              )}

              <FormControlLabel
                value={NotReportingReason.Colocated}
                control={<Radio />}
                label="This facility is co-located on the same site with another facility"
              />
              {selectedOption === NotReportingReason.Colocated && (
                <Controller
                  name="coLocatedId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FacilityPicker
                      {...field}
                      {...fieldState}
                      disabled={!!defaultValues.coLocatedId && loading}
                      value={coLocatedFacility}
                      defaultSearchTerm={`tenantId:${reports[0].tenantId}`}
                      label="Co-located Facility"
                      disabledFacilitiesById={props.reports.map((report) => report.facilityId)}
                      onChange={(fac) => {
                        setCoLocatedFacility(fac);
                        field.onChange(fac?.id ?? null);
                      }}
                    />
                  )}
                />
              )}

              <FormControlLabel
                value={NotReportingReason.UnderThreshold}
                control={<Radio />}
                label={`This facility's products were under the threshold for the entire reporting year (Jan 1, ${reportingYearText} through Dec 31, ${reportingYearText})`}
              />

              <FormControlLabel
                value={NotReportingReason.NotWithEncamp}
                control={<Radio />}
                label="This facility is not reporting through Encamp"
              />

              <FormControlLabel
                value={NotReportingReason.Exempt}
                control={<Radio />}
                label="This facility's on-site chemicals qualify for a regulatory exemption"
              />
              {selectedOption === NotReportingReason.Exempt && (
                <Controller
                  name="note"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ??
                        "Explain why this facility is exempt (e.g. it is a research lab)."
                      }
                      label="Reason for Exemption"
                      multiline
                      minRows={2}
                      fullWidth
                      required
                    />
                  )}
                />
              )}
            </RadioGroup>
          )}
        />
      </form>
    </Dialog>
  );
}
